import { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './auth-context';

interface ProtectedRouteProps {
  children: ReactNode;
  redirectTo?: string;
  requireAuth?: boolean;
}

/**
 * A component that protects routes based on authentication status
 * @param children - The components to render if access is granted
 * @param redirectTo - Where to redirect if access is denied (defaults to /login for protected routes, / for auth-only routes)
 * @param requireAuth - Whether authentication is required (true) or forbidden (false)
 */
export function ProtectedRoute({ 
  children, 
  redirectTo, 
  requireAuth = true 
}: ProtectedRouteProps) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // If still loading auth state, don't make any decisions yet
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // For protected routes (requireAuth=true)
  if (requireAuth && !isAuthenticated) {
    // Redirect to login with a return path
    return <Navigate to={redirectTo || `/login?returnTo=${encodeURIComponent(location.pathname)}`} replace />;
  }

  // For auth-only routes (requireAuth=false) like login/register
  if (!requireAuth && isAuthenticated) {
    // Redirect to home or specified path
    return <Navigate to={redirectTo || '/'} replace />;
  }

  // If access is granted, render the children
  return <>{children}</>;
}
